import { useState, useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { useSearchParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import { socket } from 'socket'
import http from 'http'

import 'maplibre-gl/dist/maplibre-gl.css'

import getRegion from 'fn/getRegion'

import Point from './Point'
import EventFilter from './EventFilter'

import { FiCrosshair, FiMoon, FiSun } from 'react-icons/fi'
import { TbTarget, TbTargetOff } from 'react-icons/tb'

const { REACT_APP_MAPLIBRE_URL_PREFIX = 'http://localhost:8071/styles' } = process.env

const Radar = () => {
  const center = [ -99.151468, 19.320772 ]
  const mapContainer = useRef()
  const map = useRef()
  const markersRef = useRef({})
  const [ searchParams ] = useSearchParams()
  const [ no, setNo ] =  useState()
  const [ points, setPoints ] = useState( [] )
  const [ follow, setFollow ] = useState( true )

  const [ darkTheme, setDarkTheme ] = useState( true )

  // init map
  useEffect( () => {
    if ( map.current ) return

    map.current = new maplibregl.Map( {
      container: mapContainer.current,
      style: `${ REACT_APP_MAPLIBRE_URL_PREFIX }/${ darkTheme ? 'dark-matter' : 'basic-preview' }/style.json`,
      center: center,
      zoom: 14,
      bearing: 0
    } )

    map.current.addControl( new maplibregl.NavigationControl(), 'top-right' )

    return () => {
      if ( map.current ) {
        map.current.remove()
        map.current = null
      }
    }

  }, [] )

  // get points
  useEffect( () => {
    http( {
      method: 'GET',
      url: '/radar'
    } )
      .then( ( { data } ) => {
        setPoints( points => {
          return data
        } )
      } )
  }, [] )

  // update no
  useEffect( () => {
    let sp = {}

    for ( let entry of searchParams.entries() ) {
      const [ key, value ] = entry

      sp[ key ] = JSON.parse( value )
    }

    setNo( sp )
  }, [ searchParams ] )

  // connect socket
  useEffect( () => {
    socket.connect()

    return () => {
      socket.disconnect()
    }
  }, [] )

  // join to radar
  useEffect( () => {
    const connectionHandler = () => {
      console.log( 'connected' )

      socket.emit( 'join', 'radar' )
    }

    socket.on( 'connect', connectionHandler )

    return () => {
      socket.off( 'connect', connectionHandler )
    }
  }, [ socket ] )

  // update points
  useEffect( () => {
    const pointHandler = ( data ) => {
      setPoints( points => {
        const pIndex = points.findIndex( p => p.user_id === data.user_id )

        data = {
          ...data,
          unix: Date.now()
        }

        if ( pIndex !== -1 ) {
          let aux = [ ...points ]

          const [ lng1, lat1 ] = aux[ pIndex ].point
          const [ lng2, lat2 ] = data.point
          // const bearing = calculateHeading( [ lng1, lat1 ], [ lng2, lat2 ] )

          aux[ pIndex ] = { ...data }

          return aux
        }

        return [ ...points, data ]
      } )
    }

    socket.on( 'point', pointHandler )

    return () => {
      socket.off( 'point', pointHandler )
    }
  }, [ socket ] )

  // destroy
  useEffect( () => {
    const destroyHandler = ( data ) => {

      console.log( { destroy: data } )

      // delete pin from map
      markersRef.current[ data.user_id ].remove()
      delete markersRef.current[ data.user_id ]

      setPoints( points => {
        const aux = [ ...points ]
          .filter( p => p.user_id !== data.user_id )

        return aux
      } )
    }

    socket.on( 'drop_pin', destroyHandler )

    return () => {
      socket.off( 'drop_pin', destroyHandler )
    }
  }, [] )

  // render points in map
  useEffect( () => {
    if ( ! map.current ) return

    points.forEach( data => {
      const { user_id, label, point: [ lng, lat ], bearing } = data

      // already on map
      if ( markersRef.current[ user_id ] ) {
        const marker = markersRef.current[ user_id ]
        // const { marker, root } = markersRef.current[ user_id ]
        // console.log( { marker, root, lo: markersRef.current[ user_id ] } )

        marker.setLngLat( [ lng, lat ] )

        // root.render(
        //   <Point config={ data } />
        // )
        // if ( bearing && bearing >= 0 )
        //   map.current.setBearing( bearing )
      }
      // create new point
      else {
        const container = document.createElement( 'div' )
        const root = createRoot( container )

        root.render(
          <Point config={ data } />
        )

        const newMarker = new maplibregl.Marker( { element: container, anchor: 'center' } )
          .setLngLat( [ lng, lat ] )
          .addTo( map.current )

        console.log( { newMarker } )
        markersRef.current[ user_id ] = newMarker
      }
    } )
    console.log( 'render to map', points )
  }, [ points ] )

  // bound
  useEffect( () => {
    if ( points.length === 0 ) return

    // console.log( 'updated points', no )

    bound()
  }, [ points ] )

  // bound if filter
  useEffect( () => {
    if ( ! map.current ) return

    bound()
  }, [ no ] )

  // bound function
  const bound = () => {
    if ( points.length === 0 ) return

    // event
    let pointsOnEvent = []
    if ( no.events && no.events.length > 0 ) {
      pointsOnEvent = points
        .filter( p => {
          const inEvent = no.events.find( e => p.event && p.event.identity === e )

          return inEvent ? true : false
        } )
    }

    // points
    let pointsInPoints = []
    if ( no.points && no.points.length > 0 ) {
      pointsInPoints = points
        .filter( p => {
          const inPoints = no.points.find( a => a === p.user_id )

          return inPoints ? true : false
        } )
    }

    let pointsToRegion = [ ...pointsInPoints, ...pointsOnEvent ]
    if ( pointsToRegion.length === 0 )
      pointsToRegion = [ ...points ]

    if ( ! follow ) return

    const region = getRegion( pointsToRegion.map( p => p.point ), 0 )

    map.current.fitBounds( region, {
      padding: 50,
      duration: 1000,
      maxZoom: 15
    } )
  }

  useEffect( () => {
    if ( ! map.current ) return

    const styleUrl = `${ REACT_APP_MAPLIBRE_URL_PREFIX }/${ darkTheme ? 'dark-matter' : 'basic-preview' }/style.json`

    map.current.setStyle( styleUrl )
  }, [ darkTheme ] )

  const calculateHeading = ( p1, p2 ) => {
    const [ lng1, lat1 ] = p1
    const [ lng2, lat2 ] = p2

    const rLat1 = ( lat1 * Math.PI ) / 180
    const rLat2 = ( lat2 * Math.PI ) / 180
    const dLng = ( ( lng2 - lng1 ) * Math.PI ) / 180

    const y = Math.sin( dLng ) * Math.cos( rLat2 )
    const x =
      Math.cos( rLat1 ) * Math.sin( rLat2 ) -
      Math.sin( rLat1 ) * Math.cos( rLat2 ) * Math.cos( dLng )

    let bearing = Math.atan2( y, x )

    bearing = ( bearing * 180 ) / Math.PI

    return ( bearing + 360 ) % 360
  }

  const toggleFollow = () => {
    setFollow( ! follow )
  }

  const toggleTheme = () => {
    setDarkTheme( ! darkTheme )
  }

  return <div className="one hs">

    <div className="bar">
      <div className="icon">
        <FiCrosshair />
      </div>

      <h1>radar</h1>

      <button
        onClick={ toggleTheme }
      >
        { darkTheme ? <FiSun /> : <FiMoon /> }
      </button>

      <button
        onClick={ toggleFollow }
      >
        { follow ? <TbTargetOff /> : <TbTarget /> }
      </button>

      <EventFilter
      />
    </div>

    <div
      ref={ mapContainer }
      className="one"
    >
    </div>

  </div>
}

export default Radar
